import { Component, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, Subscription, take } from 'rxjs';
import { AppState } from 'src/app/@store/app.reducers';
import { NotificacionInput, NotificationData } from 'src/app/models/notificacion/notificacion.models';
import { SignalRService } from 'src/app/services/signal-r/signal-r.service';

import { NotificationInboxComponent } from '../notification-inbox/notification-inbox.component';

@Component({
    selector: 'app-notification-container',
    templateUrl: './notification-container.component.html',
    styleUrls: ['./notification-container.component.scss'],
    imports: [NotificationInboxComponent]
})
export class NotificationContainerComponent implements OnInit, OnDestroy{
  public subsReduxUsuario = new Subscription();
  public notifications: NotificationData[] = [];
  private counter: number = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private timeouts: any[] = [];

  constructor(private store: Store<AppState>, 
              private signalR: SignalRService,
              private cdr: ChangeDetectorRef) {
  }

  ngOnInit() {
    // Observa cambios en usuario
    this.subsReduxUsuario.add(
      this.store.select('usuario').pipe(
        filter(data => Boolean(data.loaded && data.nombreTipoCuenta)),
        distinctUntilChanged((prev, curr) =>
          prev.nombreUsuario === curr.nombreUsuario &&
          prev.cuentaSeleccionada?.Cuenta === curr.cuentaSeleccionada?.Cuenta
        ),
        take(1) // ✅ Solo una vez se establece la conexión
      ).subscribe(data => {
        const tipoCuenta = data.cuentaSeleccionada.TipoCuenta;
        const cuenta = data.cuentaSeleccionada.Cuenta;
        const tipoPerfil = data.cuentaSeleccionada.TipoPerfil;
        const perfil = data.cuentaSeleccionada.Perfil;
        const usuario = data.nombreUsuario;
        const funciones = data.menu.flatMap(menus => menus.Funciones).flatMap(funcion => funcion.IdFuncion);

        this.signalR.loadSignalR()
          .then(() => {
            // ✅ Iniciar conexión con datos de contexto
            this.signalR.iniciarConexion(tipoCuenta, cuenta, tipoPerfil, perfil, usuario, funciones);
          })
          .catch(err => {
            console.error('Error al cargar SignalR:', err);
          });
      })
    );

    // Suscripción a notificaciones
    this.subsReduxUsuario.add(
      // 🟡 Subscripción al servicio de SignalR.
      this.signalR.notification$.subscribe((response) => {
        this.addNotification(response);
      })
    );

    // 🔹 Escucha cuando se cierra sesión y limpia todas las notificaciones
    this.subsReduxUsuario.add(
      this.signalR.logoutSubject.subscribe(() => this.clearNotifications())
    );

    // Actualizar cada minuto
    this.intervalId = setInterval(() => this.cdr.markForCheck(), 60_000);
  }

  ngOnDestroy(): void {
    this.subsReduxUsuario.unsubscribe();

    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }

    this.clearTimeouts();
  }

  public trackById(index: number, item: NotificationData): number {
    return item.id;
  }

  public addNotification(data: NotificacionInput) {
    const id = this.counter++;
    const fecha = new Date();

    this.notifications.push({ id, data, fecha});

    const tiempo = data.Contenido.TiempoVisualizacionEnSegundos;
    if (tiempo && tiempo > 0) {
      const timeoutId = setTimeout(() => this.removeNotification(id), tiempo * 1_000);
      this.timeouts.push(timeoutId);
    }
    
    this.cdr.detectChanges();
  }

  public removeNotification(id: number) {
    this.notifications = this.notifications.filter(n => n.id !== id);
    this.cdr.detectChanges();
  }

  /** 🔹 Limpia visualmente todas las notificaciones al cerrar sesión */
  private clearNotifications() {
    this.notifications = [];
    this.clearTimeouts();
    this.cdr.markForCheck();
  }

  private clearTimeouts() {
    this.timeouts.forEach(t => clearTimeout(t));
    this.timeouts = [];
  }
}
